import * as React from 'react'

import { ArrowLeftIcon, ChevronDownIcon } from 'lucide-react'

import { CaretLeftIcon } from '~/client/src/shared/components/icons/CaretLeftIcon'
import { CaretRightIcon } from '~/client/src/shared/components/icons/CaretRightIcon'
import { MapViewsIcon } from '~/client/src/shared/components/icons/MapViewsIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'
import { useHostTooltipContainer } from '~/client/src/shared/utils/hostPortalContainer'

// Click contract: a popover consumer renders its trigger through Trigger and anchors the
// sidebar to the pill; drawer consumers pass onClick. Paging leaves the sidebar or sheet
// open — a product decision — so Pagination clicks never reach a toggle or dismiss handler.

const PILL_SURFACE = [
  'tw:bg-card',
  'tw:rounded-full',
  'tw:border tw:border-solid tw:border-border',
].join(' ')

const PILL_SHADOW =
  'tw:shadow-[0px_1px_3px_1px_rgba(0,0,0,0.08),0px_1px_4px_0px_rgba(0,0,0,0.04)]'

// Caller caps max-width — pill fills its parent, except when compact.
const PILL_BASE = [
  PILL_SURFACE,
  'tw:flex tw:items-center tw:justify-between',
  'tw:h-12',
  'tw:p-1',
].join(' ')

const ICON_BUTTON = [
  'tw:flex tw:items-center tw:justify-center',
  'tw:size-10 tw:shrink-0 tw:rounded-full',
  'tw:bg-transparent tw:border-0 tw:p-0 tw:text-muted-foreground',
  'tw:cursor-pointer tw:select-none tw:outline-none',
  'tw:transition-colors tw:duration-150',
  'tw:hover:bg-muted',
  'tw:active:bg-muted',
  'tw:focus-visible:bg-muted',
].join(' ')

const ICON_BUTTON_DISABLED =
  'tw:opacity-[0.38] tw:pointer-events-none tw:cursor-default'

const TITLE_TEXT = [
  'tw:block tw:truncate',
  'tw:text-[14px] tw:leading-5 tw:font-semibold tw:tracking-[-0.084px]',
  'tw:text-foreground',
].join(' ')

const SUBTITLE_TEXT = [
  'tw:text-[11px] tw:leading-4 tw:font-medium tw:tracking-[0.08px]',
  'tw:text-muted-foreground',
].join(' ')

const COUNTER_NUMBER = [
  'tw:text-xs tw:leading-4 tw:font-medium',
  'tw:text-muted-foreground tw:tabular-nums',
].join(' ')

const SUBTITLE_DOT = 'tw:text-muted-foreground'

// A map view the user did not pick has changed under them — WCAG 3.2.5 asks that such a change be
// signalled. The name rises into place wearing the brand colour, then settles back.
// Reduce Motion is about movement, not colour: it keeps the colour and drops the rise.
const TITLE_SWITCH_CUE = [
  // w-fit so the wash hugs the name instead of running the column's full width, where it reads as
  // a loading skeleton; the padding pair insets it without moving the text.
  'tw:rounded-sm tw:w-fit tw:max-w-full tw:px-1 tw:-mx-1',
  'tw:motion-safe:animate-label-swap-in',
  'tw:motion-reduce:text-primary tw:motion-reduce:bg-primary/15',
].join(' ')

// Bold only while the cue runs: the position in the list is the fact that changed.
const COUNTER_INDEX_CUE =
  'tw:motion-safe:animate-meta-swap-weight tw:motion-reduce:font-bold'

// The counter lights up with the name so the signal spans the pill rather than one word.
const COUNTER_SWITCH_CUE = [
  'tw:rounded-sm',
  'tw:motion-safe:animate-meta-swap-tint',
  'tw:motion-reduce:text-primary tw:motion-reduce:bg-primary/15',
].join(' ')

interface IRootProps {
  className?: string
  children: React.ReactNode
  /** Drop the elevation shadow when the pill isn't floating over a map. */
  flat?: boolean
  /** Icon-only pill: sizes itself square so the caller can't turn it into an oval. */
  compact?: boolean
}

const Root: React.FC<IRootProps> = ({ className, children, flat, compact }) => (
  <div
    className={cn({
      [PILL_BASE]: true,
      'tw:w-12': compact,
      'tw:w-full': !compact,
      [PILL_SHADOW]: !flat,
      [className]: !!className,
    })}
  >
    {children}
  </div>
)

interface ITriggerProps {
  title: string
  subtitle?: string
  onClick?: () => void
  ariaLabel?: string
  /** Per Figma 12146:259816: chevron-down on hover/focus, chevron-up while sidebar is open. */
  isOpen?: boolean
  /** Force interactive styling without binding onClick (ancestor owns the toggle). Defaults to `!!onClick`. */
  isInteractive?: boolean
  /** Icon-only rendering for containers too narrow for the label; name and subtitle move to the tooltip and the accessible name. */
  compact?: boolean
  /** Set only while a map switch the user did not ask for is being signalled; a new value replays the cue. */
  switchCueKey?: number
  /** A Back button leads the pill and takes the map icon's place. */
  hideIcon?: boolean
}

type ITriggerButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  keyof ITriggerProps
>

const Trigger = React.forwardRef<
  HTMLButtonElement,
  ITriggerProps & ITriggerButtonProps
>(function Trigger(
  {
    title,
    subtitle,
    onClick,
    ariaLabel,
    isOpen = false,
    isInteractive,
    compact,
    switchCueKey,
    hideIcon,
    ...buttonProps
  },
  ref,
) {
  const interactive = isInteractive ?? !!onClick

  // Icon-only hides the name, so fold it into the tooltip and accessible name.
  const compactLabel = [title, subtitle].filter(Boolean).join(' — ')

  const Wrapper: any = interactive ? 'button' : 'div'
  const wrapperProps = interactive
    ? {
        ...buttonProps,
        type: 'button' as const,
        onClick,
        'aria-label': compact
          ? [ariaLabel, compactLabel].filter(Boolean).join(': ')
          : ariaLabel || title,
      }
    : {}

  return (
    <Wrapper
      ref={ref}
      {...wrapperProps}
      title={compact ? compactLabel : undefined}
      className={cn({
        'tw:group/pill tw:flex tw:items-center tw:gap-1 tw:min-w-0 tw:flex-1 tw:p-0 tw:border-0 tw:text-left tw:rounded-full': true,
        // Fill Root's content box so the hover circle is concentric with the pill.
        'tw:justify-center tw:self-stretch': compact,
        'tw:cursor-pointer tw:outline-none tw:transition-colors tw:duration-150 tw:hover:bg-muted tw:active:bg-muted tw:focus-visible:bg-muted':
          interactive,
        'tw:cursor-default': !interactive,
        'tw:bg-muted': isOpen,
        'tw:bg-transparent': !isOpen,
      })}
    >
      {!hideIcon && (
        <span
          className={cn({
            'tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:rounded-full tw:text-muted-foreground': true,
            'tw:size-10': !compact,
            'tw:size-full': compact,
            'tw:transition-colors tw:duration-150 tw:group-hover/pill:bg-muted tw:group-active/pill:bg-muted tw:group-focus-visible/pill:bg-muted':
              interactive,
          })}
        >
          <MapViewsIcon size="sm" />
        </span>
      )}
      {!compact && (
        <span
          className={cn({
            'tw:flex tw:flex-col tw:min-w-0 tw:flex-1 tw:pr-1': true,
            'tw:pl-1': hideIcon,
          })}
        >
          <span
            // Remounts the span so the cue replays on a second switch to the same name.
            key={switchCueKey ?? 'title'}
            className={cn({
              [TITLE_TEXT]: true,
              [TITLE_SWITCH_CUE]: switchCueKey !== undefined,
            })}
            title={title}
          >
            {title}
          </span>
          {subtitle && <SubtitleLine subtitle={subtitle} />}
        </span>
      )}
      {isInteractive && !compact && (
        // Figma 12146:259816. Width animates 0 → 40px on hover/focus/open so the
        // hidden chevron doesn't eat 40px of horizontal space from the title.
        <span
          aria-hidden="true"
          className={cn({
            'tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:h-10 tw:overflow-hidden tw:text-muted-foreground tw:transition-[width,opacity] tw:duration-150': true,
            'tw:w-0 tw:opacity-0 tw:group-hover/pill:w-10 tw:group-hover/pill:opacity-100 tw:group-focus-visible/pill:w-10 tw:group-focus-visible/pill:opacity-100':
              !isOpen,
            'tw:w-10 tw:opacity-100 tw:rotate-180': isOpen,
          })}
        >
          <ChevronDownIcon className="tw:size-5" />
        </span>
      )}
    </Wrapper>
  )
})

const SubtitleLine: React.FC<{ subtitle: string }> = ({ subtitle }) => {
  const parts = subtitle.split(' · ')
  if (parts.length < 2) {
    return (
      <span className={`${SUBTITLE_TEXT} tw:truncate`} title={subtitle}>
        {subtitle}
      </span>
    )
  }

  const [scope, ...rest] = parts
  const capability = rest.join(' · ')

  return (
    <span
      className="tw:flex tw:items-center tw:gap-1 tw:min-w-0 tw:w-full"
      title={subtitle}
    >
      {/* Symmetric truncation — `flex-shrink: 999` priority didn't parse in Tailwind v4 arbitrary values. */}
      <span className={`${SUBTITLE_TEXT} tw:truncate tw:min-w-0`}>{scope}</span>
      <span
        className={`${SUBTITLE_DOT} tw:shrink-0 tw:text-[11px] tw:leading-4`}
      >
        ·
      </span>
      <span className={`${SUBTITLE_TEXT} tw:truncate tw:min-w-0`}>
        {capability}
      </span>
    </span>
  )
}

interface IPaginationProps {
  currentIndex: number // 1-based
  total: number
  onPrev: () => void
  onNext: () => void
  isPrevDisabled?: boolean
  isNextDisabled?: boolean
  ariaPrevLabel?: string
  ariaNextLabel?: string
  /** Set only while a map switch the user did not ask for is being signalled. */
  switchCueKey?: number
}

const Pagination: React.FC<IPaginationProps> = ({
  currentIndex,
  total,
  onPrev,
  onNext,
  isPrevDisabled,
  isNextDisabled,
  ariaPrevLabel,
  ariaNextLabel,
  switchCueKey,
}) => {
  const cannotPage = total <= 1
  const prevDisabled = cannotPage || !!isPrevDisabled
  const nextDisabled = cannotPage || !!isNextDisabled
  // Clamp so a transient "no selection" (index 0) reads "1 of N", never "0 of N".
  const displayIndex = total > 0 ? Math.max(currentIndex, 1) : currentIndex

  // Split the localized counter so only the current position carries the emphasis. Every
  // translation leads with it; one that stops doing so simply renders unemphasised.
  const counterText = Localization.translator.xOfY(displayIndex, total)
  const indexText = String(displayIndex)
  const indexAt = counterText.indexOf(indexText)

  // Arrow clicks stop bubble so paging never toggles or dismisses the open sidebar or sheet.
  const handlePrev = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    onPrev()
  }
  const handleNext = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    onNext()
  }

  return (
    <div className="tw:flex tw:items-center tw:gap-1 tw:shrink-0">
      <button
        type="button"
        className={cn({
          [ICON_BUTTON]: true,
          [ICON_BUTTON_DISABLED]: prevDisabled,
        })}
        onClick={prevDisabled ? undefined : handlePrev}
        disabled={prevDisabled}
        aria-label={ariaPrevLabel || Localization.translator.previousMapView}
      >
        <CaretLeftIcon size="sm" />
      </button>
      {/* No key, unlike the title: this is a live region, and remounting it would announce the
          switch twice. The cost is that two switches inside the cue window leave the class already
          applied, so the counter does not re-flash for the second — the name still does. */}
      <span
        className={cn({
          [`${COUNTER_NUMBER} tw:px-1 tw:whitespace-nowrap`]: true,
          [COUNTER_SWITCH_CUE]: switchCueKey !== undefined,
        })}
        aria-live="polite"
      >
        {indexAt < 0 ? (
          counterText
        ) : (
          <>
            {counterText.slice(0, indexAt)}
            <span
              className={cn({
                [COUNTER_INDEX_CUE]: switchCueKey !== undefined,
              })}
            >
              {indexText}
            </span>
            {counterText.slice(indexAt + indexText.length)}
          </>
        )}
      </span>
      <button
        type="button"
        className={cn({
          [ICON_BUTTON]: true,
          [ICON_BUTTON_DISABLED]: nextDisabled,
        })}
        onClick={nextDisabled ? undefined : handleNext}
        disabled={nextDisabled}
        aria-label={ariaNextLabel || Localization.translator.nextMapView}
      >
        <CaretRightIcon size="sm" />
      </button>
    </div>
  )
}

// A way back that appears under the user is easy to miss in the pill's grey icon row: it arrives
// in the brand colour and ripples. Reduce Motion keeps the wash and drops the movement.
const BACK_CUE = [
  'tw:text-primary',
  'tw:motion-safe:animate-back-cue',
  'tw:motion-reduce:animate-back-cue-wash',
].join(' ')

interface IBackProps {
  label: string
  onClick: () => void
  isDisabled?: boolean
}

// Leads the pill, outside Trigger: a button can't nest in the trigger's own button.
const Back: React.FC<IBackProps> = ({ label, onClick, isDisabled }) => {
  const tooltipContainer = useHostTooltipContainer()
  // Stops bubble for the same reason as Pagination: it must not toggle the sidebar.
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    onClick()
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className={cn(ICON_BUTTON, BACK_CUE, {
              [ICON_BUTTON_DISABLED]: isDisabled,
            })}
            aria-label={label}
            disabled={isDisabled}
            onClick={isDisabled ? undefined : handleClick}
          />
        }
      >
        <ArrowLeftIcon aria-hidden="true" className="tw:size-5" />
      </TooltipTrigger>
      <TooltipContent
        portalContainer={tooltipContainer}
        className="tw:max-w-none tw:whitespace-nowrap"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

export const MapViewsPicker = {
  Root,
  Back,
  Trigger,
  Pagination,
}
